import { AbstractAnalyticsTracker } from '@inter-ikea-kompis/analytics';
import { IpexMomentEnum } from '@insights/insights-data-provider';
const moduleId = 'summary_page';
/**
 * Tracks events from Summary Page and sends them to a reporter.
 *
 * @event store_selector_change When store is changed, sends an event with the storeId.
 * @event selected_image_index_change When image is changed in the images tab.
 * @event tab_change When selected tab is changed.
 * @event add_to_cart_availability When add to cart button is clicked. The currently selected availability.
 * @event add_to_list_availability When add to list button is clicked. The currently selected availability.
 * @event product_collected_checked When a product is marked as collected in the product list.
 * @event product_collected_unchecked When a collected product is unchecked in the product list.
 * @event summary_page_initial_configuration When the component is rendered the first time.
 * @event book_assistance_link_click When the book assistance link is clicked.
 * @event click When edit design button or print button is clicked.
 * {@nestedTracker component-save-design-card/src/trackers/SaveDesignCardTracker.ts}.
 * {@nestedTracker component-configuration-summary/src/trackers/ConfigurationSummaryTracker.ts}.
 * {@nestedTracker component-assembly-services/src/trackers/AssemblyServicesTracker.ts}.
 */
export default class SummaryPageTracker extends AbstractAnalyticsTracker {
    constructor(options) {
        super({ moduleId, ...options });
        this.context = options.context;
    }
    sendInitialConfiguration(payload) {
        this.getReporter().report({
            event: 'summary_page_initial_configuration',
            ipexMoment: IpexMomentEnum.nonInteraction,
            payload: { ...payload, context: this.context }
        });
    }
    sendStoreSelectorChange(storeId) {
        this.getReporter().report({
            event: 'store_selector_change',
            ipexMoment: IpexMomentEnum.showMe,
            payload: { storeId, context: this.context }
        });
    }
    sendSelectedImageIndexChange(selectedIndex, numberOfImages) {
        this.getReporter().report({
            event: 'selected_image_index_change',
            ipexMoment: IpexMomentEnum.showMe,
            payload: { selectedIndex, numberOfImages, context: this.context }
        });
    }
    sendTabChange(selectedTab) {
        this.getReporter().report({
            event: 'tab_change',
            ipexMoment: IpexMomentEnum.showMe,
            payload: { selectedTab, context: this.context }
        });
    }
    sendAddToCartAvailability(zipAvailabilities, storeAvailabilities) {
        this.sendAvailabilityEvent('add_to_cart_availability', zipAvailabilities, storeAvailabilities);
    }
    sendAddToListAvailability(zipAvailabilities, storeAvailabilities) {
        this.sendAvailabilityEvent('add_to_list_availability', zipAvailabilities, storeAvailabilities);
    }
    sendHomeDeliveryClick() {
        this.getReporter().report({
            event: 'click',
            ipexMoment: IpexMomentEnum.showMe,
            payload: {
                common: { component: moduleId },
                data: {
                    action: 'choose_location'
                },
                context: this.context
            }
        });
    }
    sendCollectProduct({ collectId, collected }, vpcCode) {
        const payload = { collectId, designId: vpcCode, context: this.context };
        if (collected.includes(collectId)) {
            this.getReporter().report({
                event: 'product_collected_checked',
                ipexMoment: IpexMomentEnum.giveMe,
                payload
            });
        }
        else {
            this.getReporter().report({
                event: 'product_collected_unchecked',
                ipexMoment: IpexMomentEnum.showMe,
                payload
            });
        }
    }
    sendUnavailableProductsModalContinue(addUnavailableProductsToList) {
        this.getReporter().report({
            event: 'unavailable_products_modal_continue',
            payload: { addToList: addUnavailableProductsToList, context: this.context },
            ipexMoment: IpexMomentEnum.giveMe
        });
    }
    sendAddToCartClickWithAvailability(zipAvailabilities, storeAvailabilities) {
        const homeDeliveryStatus = this.getHomeDeliveryStatus(zipAvailabilities);
        const storeAvailabilityStatus = this.getStoreDeliveryStatus(storeAvailabilities);
        this.getReporter().report({
            event: 'add_to_cart_click',
            ipexMoment: IpexMomentEnum.giveMe,
            payload: {
                availability: {
                    homeDelivery: homeDeliveryStatus,
                    store: storeAvailabilityStatus
                },
                context: this.context
            }
        });
    }
    sendBookAssistanceLinkClick() {
        this.getReporter().report({
            event: 'book_assistance_link_click',
            ipexMoment: IpexMomentEnum.helpMe,
            payload: { context: this.context }
        });
    }
    sendPrintButtonClick() {
        this.getReporter().report({
            event: 'click',
            ipexMoment: IpexMomentEnum.showMe,
            payload: {
                common: { component: moduleId },
                data: {
                    action: 'print'
                },
                context: this.context
            }
        });
    }
    sendEditDesignButtonClick() {
        this.getReporter().report({
            event: 'click',
            ipexMoment: IpexMomentEnum.whatIsMe,
            payload: {
                common: { component: moduleId },
                data: {
                    action: 'edit_design'
                },
                context: this.context
            }
        });
    }
    /**
     * Gets the home delivery status based on current state of summaryPage.zipAvailabilities.
     *
     * - If zipAvailabilities contains only available objects, the result will be 'available'.
     * - If zipAvailabilities contains only unavailable objects, the result will be 'unavailable'.
     * - If zipAvailabilities contains both available and unavailable objects, the result will be 'mixed'.
     * - If zipAvailabilities is empty or homeDelivery is not present, the result will be 'unknown'.
     *
     * @param zipAvailabilities ...
     * @returns A {@link DeliveryStatus}.
     */
    getHomeDeliveryStatus(zipAvailabilities) {
        return (zipAvailabilities ?? []).reduce((deliveryStatus, zipAvailability) => this.maybeUpdateDeliveryStatus(deliveryStatus, zipAvailability.content.availableForHomeDelivery), 'unknown');
    }
    /**
     * Gets the store delivery status based on current state of summaryPage.storeAvailabilities.
     *
     * - If storeAvailabilities contains only available objects, the result will be 'available'.
     * - If storeAvailabilities contains only unavailable objects, the result will be 'unavailable'.
     * - If storeAvailabilities contains both available and unavailable objects, the result will be 'mixed'.
     * - If storeAvailabilities is empty, the result will be 'unknown'.
     *
     * @param storeAvailabilities ...
     * @returns A {@link DeliveryStatus}.
     */
    getStoreDeliveryStatus(storeAvailabilities) {
        return (storeAvailabilities ?? []).reduce((deliveryStatus, storeAvailability) => this.maybeUpdateDeliveryStatus(deliveryStatus, storeAvailability.content.availableForCashCarry), 'unknown');
    }
    /**
     * @param currentStatus The current accumulated status based on product availability.
     * @param availableForDelivery Whether a product is available for delivery.
     * @returns A possibly updated {@link DeliveryStatus}.
     */
    maybeUpdateDeliveryStatus(currentStatus, availableForDelivery) {
        // If already mixed, the status won't change.
        if (currentStatus === 'mixed') {
            return currentStatus;
        }
        if (availableForDelivery) {
            return currentStatus === 'unavailable' ? 'mixed' : 'available';
        }
        else {
            return currentStatus === 'available' ? 'mixed' : 'unavailable';
        }
    }
    sendAvailabilityEvent(event, zipAvailabilities, storeAvailabilities) {
        const homeDeliveryStatus = this.getHomeDeliveryStatus(zipAvailabilities);
        const storeAvailabilityStatus = this.getStoreDeliveryStatus(storeAvailabilities);
        this.getReporter().report({
            event,
            ipexMoment: IpexMomentEnum.giveMe,
            payload: {
                availability: { homeDelivery: homeDeliveryStatus, store: storeAvailabilityStatus },
                context: this.context
            }
        });
    }
}
//# sourceMappingURL=SummaryPageTracker.js.map